import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import ButtonSlim from '../../../../web-app-components/components/ButtonSlim';
import SelectInput from '../../../../web-app-components/components/SelectInput';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { DataGrid } from '@material-ui/data-grid';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';

import objectsClient from '../../../../clients/objects';
import categoriesClient from '../../../../clients/categories';
import subcategoriesClient from '../../../../clients/subcategories';

const DEFAULT_LOCALE = 'lt-LT';

/** Special value for subcategory filter: objects with no subcategory */
const SUBCATEGORY_FILTER_NONE = '__none__';

const columns = [
  {
    field: 'Title',
    headerName: 'Pavadinimas',
    flex: 1,
    sortable: false,
  },
  {
    field: 'categories',
    headerName: 'Kategorija',
    width: 180,
    sortable: false,
    valueGetter: (params) =>
      params.row.categories?.[0]?.Title ?? '—',
  },
  {
    field: 'subcategories',
    headerName: 'Subkategorijos',
    width: 200,
    sortable: false,
    valueGetter: (params) => {
      const subs = params.row.subcategories;
      if (!subs || !subs.length) return '—';
      return subs.map((s) => s.Title).join(', ');
    },
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  section: {
    padding: theme.spacing(2),
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filterSelect: {
    minWidth: 200,
    maxWidth: 280,
  },
  updateSection: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
  },
  renderValue: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const toIds = (arr) =>
  Array.isArray(arr)
    ? arr.map((v) =>
        typeof v === 'object' && v != null && 'id' in v ? v.id : v,
      )
    : [];

const ObjectsBulkUpdate = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState([]);
  const [selectedSubcategoryFilter, setSelectedSubcategoryFilter] = useState([]);
  const [categories, setCategories] = useState();
  const [subcategories, setSubcategories] = useState();
  const [objects, setObjects] = useState();
  const [selectionModel, setSelectionModel] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newSubcategories, setNewSubcategories] = useState([]);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setCategories(await categoriesClient.find({ _sort: 'Order:asc' }));
      } catch (error) {
        enqueueSnackbar(
          'Nepavyko gauti kategorijų. Bandykite atidaryti iš naujo.',
          { variant: 'error' },
        );
      }
    })();
  }, [enqueueSnackbar]);

  useEffect(() => {
    (async () => {
      try {
        const data = await subcategoriesClient.find({ sort: 'Order:asc' });
        setSubcategories(data);
      } catch (error) {
        enqueueSnackbar(
          'Nepavyko gauti subkategorijų. Bandykite atidaryti iš naujo.',
          { variant: 'error' },
        );
        setSubcategories([]);
      }
    })();
  }, [enqueueSnackbar]);

  useEffect(() => {
    (async () => {
      try {
        const query = { sort: 'createdAt:desc' };
        const filtersObj = {};

        if (selectedCategoryFilter && selectedCategoryFilter.length > 0) {
          filtersObj.categories = {
            documentId: { $in: selectedCategoryFilter },
          };
        }
        const subIds = (selectedSubcategoryFilter || []).filter(
          (id) => id !== SUBCATEGORY_FILTER_NONE,
        );
        const subNone = (selectedSubcategoryFilter || []).includes(
          SUBCATEGORY_FILTER_NONE,
        );
        if (subNone && subIds.length === 0) {
          filtersObj.subcategories = { $null: true };
        } else if (subNone && subIds.length > 0) {
          filtersObj.$or = [
            { subcategories: { $null: true } },
            {
              subcategories: {
                documentId: { $in: subIds },
              },
            },
          ];
        } else if (subIds.length > 0) {
          filtersObj.subcategories = {
            documentId: { $in: subIds },
          };
        }
        if (Object.keys(filtersObj).length > 0) {
          query.filters = filtersObj;
        }

        setObjects(await objectsClient.find(query));
      } catch (error) {
        enqueueSnackbar(
          'Nepavyko gauti objektų sąrašo. Bandykite atidaryti iš naujo.',
          { variant: 'error' },
        );
      }
    })();
  }, [
    selectedCategoryFilter,
    selectedSubcategoryFilter,
    enqueueSnackbar,
  ]);

  const handleCategoryFilterChange = ({ target: { value } }) => {
    setSelectedCategoryFilter(
      typeof value === 'string' ? value.split(',') : value,
    );
    setSelectionModel([]);
  };

  const handleSubcategoryFilterChange = ({ target: { value } }) => {
    setSelectedSubcategoryFilter(
      typeof value === 'string' ? value.split(',') : value,
    );
    setSelectionModel([]);
  };

  const handleNewCategoryChange = ({ target: { value } }) => {
    setNewCategory(value || '');
    setNewSubcategories([]);
  };

  const handleNewSubcategoriesChange = ({ target: { value } }) => {
    setNewSubcategories(typeof value === 'string' ? value.split(',') : value);
  };

  const subcategoriesForSelectedCategory =
    newCategory && subcategories
      ? subcategories.filter(
          (s) => (s.category?.id ?? s.category) === newCategory,
        )
      : [];

  const hasSubcategoriesForNewCategory =
    categories?.find((c) => c.id === newCategory)?.hasSubcategories;

  const handleBulkUpdate = async () => {
    if (selectionModel.length === 0) {
      enqueueSnackbar('Pasirinkite bent vieną objektą.', {
        variant: 'warning',
      });
      return;
    }
    if (!newCategory) {
      enqueueSnackbar('Pasirinkite naują kategoriją.', { variant: 'warning' });
      return;
    }

    setUpdating(true);
    let successCount = 0;
    let errorCount = 0;

    const categoryPayload = newCategory ? [newCategory] : [];
    const subcategoryPayload = Array.isArray(newSubcategories)
      ? newSubcategories
      : [];

    for (const objectId of selectionModel) {
      try {
        await objectsClient.update(
          objectId,
          {
            categories: categoryPayload,
            subcategories: subcategoryPayload,
          },
          DEFAULT_LOCALE,
        );
        successCount += 1;
      } catch (error) {
        errorCount += 1;
        console.error('Bulk update failed for object', objectId, error);
      }
    }

    setUpdating(false);
    setSelectionModel([]);

    if (errorCount === 0) {
      enqueueSnackbar(
        `Atnaujinta ${successCount} objektų kategorija ir subkategorijos.`,
        { variant: 'success' },
      );
      try {
        const query = { sort: 'createdAt:desc' };
        const filtersObj = {};
        if (selectedCategoryFilter?.length > 0) {
          filtersObj.categories = {
            documentId: { $in: selectedCategoryFilter },
          };
        }
        const refetchSubIds = (selectedSubcategoryFilter || []).filter(
          (id) => id !== SUBCATEGORY_FILTER_NONE,
        );
        const refetchSubNone = (selectedSubcategoryFilter || []).includes(
          SUBCATEGORY_FILTER_NONE,
        );
        if (refetchSubNone && refetchSubIds.length === 0) {
          filtersObj.subcategories = { $null: true };
        } else if (refetchSubNone && refetchSubIds.length > 0) {
          filtersObj.$or = [
            { subcategories: { $null: true } },
            {
              subcategories: {
                documentId: { $in: refetchSubIds },
              },
            },
          ];
        } else if (refetchSubIds.length > 0) {
          filtersObj.subcategories = {
            documentId: { $in: refetchSubIds },
          };
        }
        if (Object.keys(filtersObj).length > 0) query.filters = filtersObj;
        setObjects(await objectsClient.find(query));
      } catch (refetchErr) {
        console.warn('Refetch after bulk update failed', refetchErr);
      }
    } else {
      enqueueSnackbar(
        `Atnaujinta ${successCount} objektų. Klaidų: ${errorCount}.`,
        { variant: 'warning' },
      );
    }
  };

  const renderCategoryValue = (selected) => {
    const ids = toIds(selected);
    if (ids.length === 0) return '';
    const map = categories
      ? categories.reduce((acc, c) => {
          acc[c.id] = c.Title;
          return acc;
        }, {})
      : {};
    const labels = ids.map((id) => map[id] || '').filter(Boolean);
    const text =
      labels.length === 1 ? labels[0] : `${labels.length} pasirinkta`;
    return (
      <span className={classes.renderValue} title={labels.join(', ')}>
        {text}
      </span>
    );
  };

  const renderSubcategoryValue = (selected) => {
    const ids = toIds(selected);
    if (ids.length === 0) return '';
    const hasNone = ids.includes(SUBCATEGORY_FILTER_NONE);
    const realIds = ids.filter((id) => id !== SUBCATEGORY_FILTER_NONE);
    const map = subcategories
      ? subcategories.reduce((acc, s) => {
          acc[s.id] = s.Title;
          return acc;
        }, {})
      : {};
    const labels = [
      ...(hasNone ? ['Be subkategorijų'] : []),
      ...realIds.map((id) => map[id] || '').filter(Boolean),
    ];
    const text =
      labels.length === 1 ? labels[0] : `${labels.length} pasirinkta`;
    return (
      <span className={classes.renderValue} title={labels.join(', ')}>
        {text}
      </span>
    );
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.section}>
        Objektų kategorijų masinis atnaujinimas
      </Typography>

      <Paper className={classes.section}>
        <Typography variant="subtitle1" gutterBottom>
          Filtruoti objektus
        </Typography>
        <div className={classes.controls}>
          {categories ? (
            <SelectInput
              className={classes.filterSelect}
              label="Kategorija"
              multiple
              value={selectedCategoryFilter}
              onChange={handleCategoryFilterChange}
              renderValue={renderCategoryValue}
            >
              {categories.map(({ id, Title }) => (
                <MenuItem key={id} value={id}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedCategoryFilter.indexOf(id) !== -1}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={Title} />
                </MenuItem>
              ))}
            </SelectInput>
          ) : (
            <CircularProgress size={24} />
          )}
          {subcategories ? (
            <SelectInput
              className={classes.filterSelect}
              label="Subkategorija"
              multiple
              value={selectedSubcategoryFilter}
              onChange={handleSubcategoryFilterChange}
              renderValue={renderSubcategoryValue}
            >
              <MenuItem value={SUBCATEGORY_FILTER_NONE}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={
                      selectedSubcategoryFilter.indexOf(SUBCATEGORY_FILTER_NONE) !== -1
                    }
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary="Be subkategorijų (NONE)" />
              </MenuItem>
              {subcategories.map(({ id, Title }) => (
                <MenuItem key={id} value={id}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={
                        selectedSubcategoryFilter.indexOf(id) !== -1
                      }
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={Title} />
                </MenuItem>
              ))}
            </SelectInput>
          ) : (
            <CircularProgress size={24} />
          )}
        </div>

        <DataGrid
          rows={objects || []}
          columns={columns}
          loading={!objects}
          autoHeight
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          checkboxSelection
          selectionModel={selectionModel}
          onSelectionModelChange={({ selectionModel: next }) =>
            setSelectionModel(next)
          }
          disableColumnMenu
        />
      </Paper>

      <Paper className={classes.section}>
        <Typography variant="subtitle1" gutterBottom>
          Nustatyti kategoriją ir subkategorijas pasirinktiems objektams
        </Typography>
        <Box className={classes.updateSection}>
          {categories ? (
            <SelectInput
              className={classes.filterSelect}
              label="Nauja kategorija"
              value={newCategory}
              onChange={handleNewCategoryChange}
            >
              <MenuItem value="">
                <em>— nekeisti —</em>
              </MenuItem>
              {categories.map(({ id, Title }) => (
                <MenuItem key={id} value={id}>
                  {Title}
                </MenuItem>
              ))}
            </SelectInput>
          ) : null}
          {hasSubcategoriesForNewCategory &&
          subcategoriesForSelectedCategory.length > 0 ? (
            <SelectInput
              className={classes.filterSelect}
              label="Naujos subkategorijos"
              multiple
              value={newSubcategories}
              onChange={handleNewSubcategoriesChange}
              renderValue={renderSubcategoryValue}
            >
              {subcategoriesForSelectedCategory.map(({ id, Title }) => (
                <MenuItem key={id} value={id}>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={newSubcategories.indexOf(id) !== -1}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={Title} />
                </MenuItem>
              ))}
            </SelectInput>
          ) : null}
          <ButtonSlim
            variant="contained"
            color="primary"
            disabled={updating || selectionModel.length === 0}
            onClick={handleBulkUpdate}
          >
            {updating
              ? 'Atnaujinama…'
              : `Atnaujinti (${selectionModel.length})`}
          </ButtonSlim>
        </Box>
      </Paper>
    </div>
  );
};

export default ObjectsBulkUpdate;
